<template>
  <b-card class="filters-card">
    <b-row>
      <b-col md="4">
        <b-form-group label="Поиск">
          <b-form-input
            v-model="localFilters.search"
            placeholder="Номер, ФИО участника..."
            @input="emitUpdate"
          />
        </b-form-group>
      </b-col>

      <b-col md="2">
        <b-form-group label="Пол">
          <b-form-select
            v-model="localFilters.gender"
            :options="genderOptions"
            @change="emitUpdate"
          />
        </b-form-group>
      </b-col>

      <b-col md="2">
        <b-form-group label="Группа">
          <b-form-select
            v-model="localFilters.group"
            :options="groupOptions"
            @change="emitUpdate"
          />
        </b-form-group>
      </b-col>

      <b-col md="2">
        <b-form-group label="Статус">
          <b-form-select
            v-model="localFilters.status"
            :options="statusOptions"
            @change="emitUpdate"
          />
        </b-form-group>
      </b-col>

      <b-col md="2">
        <b-form-group label="Ограничения">
          <b-form-checkbox
            v-model="localFilters.violationOnly"
            @change="emitUpdate"
          >
            Только нарушения
          </b-form-checkbox>
        </b-form-group>
      </b-col>

      <b-col
        md="2"
        class="d-flex align-items-end justify-content-end"
      >
        <b-button
          variant="outline-secondary"
          size="sm"
          class="mb-3"
          @click="resetFilters"
        >
          Сбросить
        </b-button>
      </b-col>
    </b-row>

    <b-row
      v-if="showRankingByChipForGroupFilter"
      class="mt-2"
    >
      <b-col cols="12">
        <b-alert
          variant="info"
          show
          class="mb-0 py-2 small"
        >
          <b-icon
            icon="info-circle"
            class="mr-1"
          />
          <strong>Номера мест (колонка «№»):</strong>
          выбрана одна возрастная группа, для дистанции включён официальный
          <strong>Chip Time</strong>
          — порядок строк в таблице результатов и нумерация мест считаются
          <strong>по чип-тайму</strong>, а не по времени от старта гонки.
        </b-alert>
      </b-col>
    </b-row>

    <!-- Result type toggle (hidden when results grid shows gun + chip columns) -->
    <b-row
      v-if="hasStartCheckpoint && !hideGunChipToggle"
      class="mt-3"
    >
      <b-col md="12">
        <b-form-group>
          <template #label>
            Тип результата
            <b-icon
              v-b-tooltip.hover.right="'Gun Time - от старта гонки. Chip Time - от START чекпоинта (учитывает очередь на старте).'"
              icon="info-circle"
              class="ml-1 text-primary"
              font-scale="0.9"
            />
          </template>
          <b-form-radio-group
            v-model="localFilters.resultType"
            :options="resultTypeOptions"
            button-variant="outline-primary"
            buttons
            size="sm"
            @change="emitUpdate"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Time display mode toggle -->
    <b-row class="mt-2">
      <b-col md="12">
        <b-form-group label="Отображение времени на чекпоинтах">
          <b-form-radio-group
            v-model="localFilters.timeDisplayMode"
            :options="timeDisplayOptions"
            button-variant="outline-primary"
            buttons
            size="sm"
            @change="emitUpdate"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormRadioGroup,
  BButton,
  BIcon,
  VBTooltip,
  BFormCheckbox,
  BAlert,
} from 'bootstrap-vue'

export default {
  name: 'ResultsFilters',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormRadioGroup,
    BButton,
    BIcon,
    BFormCheckbox,
    BAlert,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    initialFilters: {
      type: Object,
      default: null,
    },
    results: {
      type: Array,
      default: () => [],
    },
    /** When set (from GET .../filters), group dropdown uses full list for the distance */
    groupOptionsFromMeta: {
      type: Array,
      default: null,
    },
    /** When true, ResultsGrid shows both gun and chip columns — Gun/Chip switch is redundant */
    hideGunChipToggle: {
      type: Boolean,
      default: false,
    },
    /** Distance config: when `chipTime`, server ranks filtered-by-group results by chip */
    officialTimeMode: {
      type: String,
      default: 'gunTime',
    },
  },
  data() {
    return {
      localFilters: {
        search: '',
        gender: '',
        group: '',
        status: '',
        violationOnly: false,
        resultType: 'gun', // 'gun' or 'chip'
        chipTimeEnabled: true,
        timeDisplayMode: 'split',
      },
      timeDisplayOptions: [
        { text: 'Сплит (между чекпоинтами)', value: 'split' },
        { text: 'От старта (накопительно)', value: 'cumulative' },
      ],
      resultTypeOptions: [
        { text: '⏱️ По стартовому времени (Gun Time)', value: 'gun' },
        { text: '🏃 По чипу (Chip Time)', value: 'chip' },
      ],
    }
  },
  computed: {
    genderOptions() {
      return [
        { value: '', text: 'Все' },
        { value: 'М', text: 'Мужчины' },
        { value: 'Ж', text: 'Женщины' },
      ]
    },

    groupOptions() {
      if (this.groupOptionsFromMeta != null && Array.isArray(this.groupOptionsFromMeta)) {
        const options = [{ value: '', text: 'Все группы' }]
        this.groupOptionsFromMeta
          .filter(g => g != null && String(g).trim() !== '')
          .sort()
          .forEach(g => {
            options.push({ value: g, text: g })
          })
        return options
      }

      const groups = new Set()
      this.results.forEach(r => {
        if (r.group) groups.add(r.group)
      })

      const options = [{ value: '', text: 'Все группы' }]
      Array.from(groups).sort().forEach(g => {
        options.push({ value: g, text: g })
      })

      return options
    },

    statusOptions() {
      return [
        { value: '', text: 'Все статусы' },
        { value: 'finished', text: 'Финишировал' },
        { value: 'dnf', text: 'НФ' },
        { value: 'dsq', text: 'ДСК' },
        { value: 'dns', text: 'НС' },
      ]
    },

    hasStartCheckpoint() {
      // Chip time column available when any row has chipTime (START-based or first/last CP in chip mode)
      return this.results.some(r => r.chipTime)
    },

    showRankingByChipForGroupFilter() {
      return String(this.localFilters.group || '').trim() !== '' && this.officialTimeMode === 'chipTime'
    },
  },
  mounted() {
    if (this.initialFilters && typeof this.initialFilters === 'object') {
      this.localFilters = {
        ...this.localFilters,
        ...this.initialFilters,
        chipTimeEnabled: this.initialFilters.chipTimeEnabled !== false,
      }
      this.emitUpdate()
    }
  },
  methods: {
    emitUpdate() {
      this.$emit('update', { ...this.localFilters })
    },

    resetFilters() {
      this.localFilters = {
        search: '',
        gender: '',
        group: '',
        status: '',
        violationOnly: false,
        resultType: 'gun',
        chipTimeEnabled: true,
        timeDisplayMode: 'split',
      }
      this.emitUpdate()
    },
  },
}
</script>

<style scoped>
.filters-card {
  margin-bottom: 1rem;
}
</style>
